import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import { DataGrid } from '@mui/x-data-grid';
import { useSelector, useDispatch } from "react-redux";
import { Grid } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import * as statusDispatch from '../../../store/extractor/actions';
import { getExtractorStatusSelector, getExtractorStatusPendingSelector } from "../../../store/extractor/selectors";
import { convertUTCDateToLocalDate } from '../../shared/functions'
import * as workflows from '../../../store/workFlows/actions'
import { postWorkflowsPendingSelector } from '../../../store/workFlows/selectors'
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import { getRoleClaimsSelector } from '../../../store/auth/selectors';
import { ISingleExtractorStatus } from '../../../store/extractor/types';

export type StatusProps = {
    batchCode: string,
    enabled?: boolean
};

const ExtractorStatus = (props: StatusProps) => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [dataRows, setDataRows] = useState<any>([]);
    const [open, setOpen] = React.useState(false);
    const { batchCode } = props

    const handleClose = () => setOpen(false);
    const dispatch = useDispatch()
    const statusList = useSelector(getExtractorStatusSelector);
    const statusPending = useSelector(getExtractorStatusPendingSelector);
    const workflowPending = useSelector(postWorkflowsPendingSelector);
    const [running, setRunning] = useState(false)
    const [rebase, setRebase] = useState(false)
    const [maximizedHeight, setHeightMaximized] = useState(false);
    const [maximizedWidth, setMaximizedWidth] = useState(false);

    // roles  - IDP.Admin or IDP.User
    const roles: string[] = useSelector(getRoleClaimsSelector);

    const refreshStatus = () => {
        dispatch(statusDispatch.fetchExtractorStatusReq(props.batchCode));
    }

    const handleOpen = () => {
        setOpen(true)
        refreshStatus()
    };

    // Set IsAdmin
    useEffect(() => {
        if (roles && roles.length > 0) {
            setIsAdmin(roles.includes("IDP.Admin"));
        }
    }, [roles]);

    useEffect(() => {
        if (statusList && statusList.length > 0) {
            const newRows = statusList.map((item: ISingleExtractorStatus, index: number) => {
                return {
                    ...item, id: index, batchStart: convertUTCDateToLocalDate(item.batchStart),
                    actualLogTime: convertUTCDateToLocalDate(item.actualLogTime), exception: item.exception === null ? 'NA' : item.exception
                };
            });
            setDataRows(newRows);
        }

        if (statusPending !== true) {
            const isRunning = statusList?.some(item => item.status === "Executing");
            setRunning(!!isRunning);
        }
    }, [statusList]);

    const runWorkflow = () => {
        const finalExtractorRun = {
            "batchCode": batchCode ? batchCode : '',
            "rebase": false
        }
        setRebase(false)
        dispatch(workflows.postWorkFlowsRequest(finalExtractorRun));
        setRunning(true);
    }

    const runRebase = () => {
        const finalExtractorRun = {
            "batchCode": batchCode ? batchCode : '',
            "rebase": true
        }
        setRebase(true)
        dispatch(workflows.postWorkFlowsRequest(finalExtractorRun));
        setRunning(true);
    }

    const style = {
        position: 'relative',
        top: '45%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: maximizedWidth ? '100%' : '60%',
        background: 'white',
        height: maximizedHeight ? '100%' : '60%',
        p: 4,
    };

    const toggleMaximize = () => {
        setHeightMaximized(!maximizedHeight);
        setMaximizedWidth(!maximizedWidth);
    };

    const buttonStyle = {
        background: 'white',
        ml: { xs: -1, sm: -2, md: -3, lg: -4 },
        mr: { xs: -1, sm: -2, md: -3, lg: -4 },
        mt: { xs: 0, sm: 1, md: 2, lg: 3 },
        p: 1.5,
    };

    return (
        <>
            {isAdmin && (
                <>
                    {workflowPending && !rebase ? (
                        <LoadingButton loading loadingPosition="start" variant="outlined" sx={{ m: 1 }} startIcon={<SaveIcon />}>Processing</LoadingButton>
                    ) : (
                            <Button id="6b146e18-0da6-465c-8e5b-ce488c440f8a" color="primary" variant="contained" onClick={runWorkflow} disabled={!props.enabled || running} sx={{ m: 1 }}>Run now</Button>
                    )}
                    {workflowPending && rebase ? (
                        <LoadingButton loading loadingPosition="start" variant="outlined" sx={{ m: 1 }} startIcon={<SaveIcon />}>Processing</LoadingButton>
                    ) : (
                            <Button id="0ecc15a7-48d6-4366-a0be-3ef67cb4d6d5" color="primary" variant="contained" onClick={runRebase} disabled={!props.enabled || running} sx={{ m: 1 }}>Run now (rebase)</Button>
                    )}
                </>
            )}

            <Button id="62a28e96-d6a4-436b-97dc-a62f5edea0ee"
                color="primary"
                variant="contained"
                onClick={handleOpen}
                sx={{ m: 1 }}
            >
                Extractor Status
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >   
                <Box sx={style}>
                    {statusPending ? (
                        <Grid container width="100%">
                            {[...Array(7)].map((_, index) => (
                                <Grid item xs={12} key={index}>
                                    <Skeleton animation="wave" height={60} />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                            <Typography id="modal-modal-description" >
                            <DataGrid
                                pagination
                                rows={dataRows}
                                sortingOrder={['asc', 'desc']}
                                sx={{
                                    wordBreak: "break-word",
                                }}
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: 'batchStart', sort: 'desc' }],
                                    },
                                }}
                                rowsPerPageOptions={[30, 50, 100]}
                                columns={[
                                    {
                                        field: 'batchStart',
                                        headerName: 'Start Time',
                                        width: 150,
                                    },
                                    {
                                        field: 'status',
                                        headerName: 'Status',
                                        width: 150,
                                    },
                                    {
                                        field: 'exception',
                                        headerName: 'Exception',
                                        width: 350,
                                    },
                                    {
                                        field: 'runTimeInSeconds',
                                        headerName: 'Run Time (seconds)',
                                        width: 150,
                                    },
                                    {
                                        field: 'isRebase',
                                        headerName: 'Rebase',
                                        width: 150,
                                    },
                                    {
                                        field: 'recordCount',
                                        headerName: 'Record Count',
                                        width: 150,
                                    },
                                ]}
                            />

                                <Box sx={{ ...buttonStyle, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button id="98351a58-d2ee-4f5a-8d6c-d32b4e5da231" color="primary" variant="contained" onClick={refreshStatus}>Refresh</Button>
                                    <Button id="36f8aac1-f9b5-48ad-980b-1f2d980b0e02" color="primary" variant="contained" onClick={handleClose} sx={{ ml: 1 }}>Close</Button>
                                    <Button color="primary" variant="contained" onClick={toggleMaximize} sx={{ ml: 1 }}>{maximizedHeight ? 'Minimize' : 'Maximize'}</Button>
                            </Box>
                        </Typography>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default ExtractorStatus;
